import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { RootState, AppDispatch } from '@/redux/store';
import type { Agent } from '@/types/Agents';
import {
  updateAgentSettings,
  deleteAgent,
  selectAgentById,
} from '@/redux/agentsSlice';
import TitleInput from '@/components/page-components/project/agents/ui/TitleInput';
import ContextInput from '@/components/page-components/project/new-agent-form/ContextInput';
import DescriptionInput from '@/components/page-components/project/new-agent-form/DescriptionInput';
import FormRow from '@/components/common/FormRow';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { CardTitle } from '@/components/ui/card';
import SubscriptionPlans from '@/components/page-components/project/agents/SubscriptionPlans';
import {
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Clock,
  Loader2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  PHONE_COUNTRY_OPTIONS,
  getAgentPhoneNumber,
  getPhoneLengthMessage,
  isValidNotificationLocalNumber,
  parsePhoneNumber,
} from '@/utils/phoneUtils';
import NotificationPhoneField from '@/components/page-components/project/agents/ui/NotificationPhoneField';
import SummaryDigestField, {
  DEFAULT_DIGEST_HOUR,
} from '@/components/page-components/project/agents/ui/SummaryDigestField';
import CopyablePhoneNumber from '@/components/common/CopyablePhoneNumber';
import StickySaveBar from '@/components/common/StickySaveBar';
import { useDateFormat } from '@/hooks/useDateFormat';

const SECTION_CARD_CLASS =
  'w-full bg-card p-3 max-lg:px-3 sm:space-y-6 sm:p-6 gap-4 space-y-4';

interface AgentSettingsProps {
  agentId: number;
  readOnly?: boolean;
  showSubscriptionOptions?: boolean;
  onSubscriptionRequested?: () => void;
  onCancelSubscription?: () => void | Promise<void>;
  isCancellingSubscription?: boolean;
}

const AgentSettings = ({
  agentId,
  readOnly = false,
  showSubscriptionOptions = false,
  onSubscriptionRequested,
  onCancelSubscription,
  isCancellingSubscription = false,
}: AgentSettingsProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const agent = useSelector((state: RootState) =>
    selectAgentById(state, agentId)
  );
  const { t } = useTranslation();
  const df = useDateFormat();

  const [agentName, setAgentName] = useState(agent?.name || '');
  const [context, setContext] = useState(agent?.context || '');
  const [description, setDescription] = useState(agent?.description || '');
  const initialPhone = parsePhoneNumber(agent?.notification_phone_number);
  const [notificationCountryCode, setNotificationCountryCode] = useState(
    initialPhone.code
  );
  const [notificationLocalNumber, setNotificationLocalNumber] = useState(
    initialPhone.local
  );
  const [digestEnabled, setDigestEnabled] = useState(
    agent?.config?.notifications?.summary_email_delivery === 'digest'
  );
  const [digestHour, setDigestHour] = useState(
    agent?.config?.notifications?.summary_digest_hour ?? DEFAULT_DIGEST_HOUR
  );
  const [isMoreSettingsOpen, setIsMoreSettingsOpen] = useState(false);
  const moreOptionsRef = useRef<HTMLDivElement>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const notificationCountryOptions = PHONE_COUNTRY_OPTIONS.filter(
    option => !['US', 'DE', 'UK'].includes(option.label.split(' ')[0] ?? '')
  );

  useEffect(() => {
    if (!isMoreSettingsOpen) return;

    const scrollToMoreOptions = () => {
      const target = moreOptionsRef.current;
      if (!target) return;

      const mainScroller = target.closest('main');
      if (mainScroller) {
        const top =
          target.getBoundingClientRect().bottom -
          mainScroller.getBoundingClientRect().top +
          mainScroller.scrollTop;
        mainScroller.scrollTo({
          top: top - mainScroller.clientHeight + 32,
          behavior: 'smooth',
        });
        return;
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };

    const frame = requestAnimationFrame(() => {
      setTimeout(scrollToMoreOptions, 200);
    });
    return () => cancelAnimationFrame(frame);
  }, [isMoreSettingsOpen]);

  useEffect(() => {
    if (agent) {
      setAgentName(agent.name);
      setContext(agent.context);
      setDescription(agent.description || '');
      const parsed = parsePhoneNumber(agent.notification_phone_number);
      setNotificationCountryCode(parsed.code);
      setNotificationLocalNumber(parsed.local);
      setDigestEnabled(
        agent.config?.notifications?.summary_email_delivery === 'digest'
      );
      setDigestHour(
        agent.config?.notifications?.summary_digest_hour ?? DEFAULT_DIGEST_HOUR
      );
    }
  }, [agent]);

  // Comparing against the agent rather than against first render, so the bar
  // settles back to clean after a save moves the stored values.
  const savedSnapshot = JSON.stringify({
    name: agent?.name ?? '',
    description: agent?.description ?? '',
    context: agent?.context ?? '',
    phone: agent?.notification_phone_number ?? '',
    digest: agent?.config?.notifications?.summary_email_delivery === 'digest',
    hour:
      agent?.config?.notifications?.summary_digest_hour ?? DEFAULT_DIGEST_HOUR,
  });

  const currentSnapshot = JSON.stringify({
    name: agentName,
    description,
    context,
    phone: notificationLocalNumber
      ? `${notificationCountryCode}${notificationLocalNumber}`
      : '',
    digest: digestEnabled,
    hour: digestHour,
  });

  const isDirty = Boolean(agent) && savedSnapshot !== currentSnapshot;

  const handleSave = async () => {
    if (
      notificationLocalNumber &&
      !isValidNotificationLocalNumber(
        notificationLocalNumber,
        notificationCountryCode
      )
    ) {
      const lengthMessage = getPhoneLengthMessage(notificationCountryCode);
      toast({
        variant: 'destructive',
        title: t('agent.settings.save_error.title.translation', 'Save Failed'),
        description: t(lengthMessage.key, {
          defaultValue: lengthMessage.defaultValue,
          ...lengthMessage.values,
        }),
      });
      return;
    }

    try {
      const nextConfig: Agent['config'] = {
        ...(agent?.config ?? {}),
        notifications: {
          ...(agent?.config?.notifications ?? {}),
          summary_email_delivery: digestEnabled
            ? ('digest' as const)
            : ('immediate' as const),
          summary_digest_hour: digestHour,
        },
      };

      await dispatch(
        updateAgentSettings({
          agentId,
          settings: {
            name: agentName,
            context,
            description,
            notification_phone_number: notificationLocalNumber
              ? `${notificationCountryCode}${notificationLocalNumber}`
              : null,
            config: nextConfig,
          },
        })
      ).unwrap();

      toast({
        title: t('agent.settings.save_success.title.translation'),
      });
    } catch (err) {
      console.error('Failed to update agent settings:', err);
      toast({
        variant: 'destructive',
        title: t('agent.settings.save_error.title.translation'),
        description: t('agent.settings.errors.unknown_error.translation', {
          error:
            err instanceof Error
              ? err.message
              : t('agent.settings.errors.unknown_error.translation'),
        }),
      });
    }
  };

  // An agent is only deletable once its plan is not active: that is what keeps
  // a hidden agent from going on answering calls and billing to period end,
  // since `deleted` alone does not stop either.
  const hasActiveSubscription = agent?.subscription?.status === 'active';
  const expectedDeleteName = (agent?.name ?? '').trim();
  const isDeleteNameConfirmed =
    expectedDeleteName.length > 0 &&
    deleteConfirmName.trim().toLocaleLowerCase() ===
      expectedDeleteName.toLocaleLowerCase();

  const handleDeleteDialogOpenChange = (open: boolean) => {
    setShowDeleteDialog(open);
    if (!open) {
      setDeleteConfirmName('');
      setError(null);
    }
  };

  const handleDelete = async () => {
    // The dialog disables the button in both cases; re-checked here so the
    // destructive call is never reachable by a stray keypress or a stale render.
    if (hasActiveSubscription || !isDeleteNameConfirmed) return;

    setError(null);
    setIsDeleting(true);

    try {
      await dispatch(deleteAgent(agentId)).unwrap();

      toast({
        title: t('agent.settings.delete_success.title.translation'),
        description: t('agent.settings.delete_success.description.translation'),
      });
      setShowDeleteDialog(false);
      navigate('/home');
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t('agent.settings.errors.unknown_error.translation');
      setError(
        t('agent.settings.errors.unknown_error.translation', {
          error: errorMessage,
        })
      );
      toast({
        variant: 'destructive',
        title: t('agent.settings.delete_error.title.translation'),
        description: t('agent.settings.errors.unknown_error.translation', {
          error: errorMessage,
        }),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReset = () => {
    if (agent) {
      setAgentName(agent.name);
      setContext(agent.context);
      setDescription(agent.description || '');
      const parsed = parsePhoneNumber(agent.notification_phone_number);
      setNotificationCountryCode(parsed.code);
      setNotificationLocalNumber(parsed.local);
      setDigestEnabled(
        agent.config?.notifications?.summary_email_delivery === 'digest'
      );
      setDigestHour(
        agent.config?.notifications?.summary_digest_hour ?? DEFAULT_DIGEST_HOUR
      );

      toast({
        title: t('agent.settings.reset_success.title.translation'),
        description: t('agent.settings.reset_success.description.translation'),
      });
    }
  };

  const handleSubscriptionRequested = async () => {
    if (onSubscriptionRequested) {
      await onSubscriptionRequested();
    }
  };

  const renderReadOnlyBanner = () => (
    <div className="rounded-lg bg-card p-3 sm:p-4">
      <div className="flex items-start gap-2 sm:gap-3">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {t(
            'agent.settings.readonly',
            'Settings are currently read-only. Please subscribe or wait for your subscription to be activated.'
          )}
        </p>
      </div>
    </div>
  );

  const agentPhoneNumber = getAgentPhoneNumber(agent);

  const renderAgentDetailsForm = (disabled: boolean) => (
    <Card className={SECTION_CARD_CLASS}>
      <div>
        <CardTitle
          description={t(
            'agent.settings.description.translation',
            'Update your agent name, description, context, and notification settings.'
          )}
        >
          {t('agent.settings.title.translation', 'Agent Settings')}
        </CardTitle>
      </div>
      <div className="flex flex-col gap-3 sm:gap-4">
        <FormRow label={t('agent.new.name.translation')}>
          <TitleInput
            value={agentName}
            onChange={setAgentName}
            disabled={disabled}
          />
        </FormRow>
        <FormRow
          label={t(
            'agent.settings.phone_number.translation',
            'Agent Phone Number'
          )}
        >
          {agentPhoneNumber ? (
            <div className="flex flex-col gap-1">
              <CopyablePhoneNumber number={agentPhoneNumber} />
              <p className="px-1 text-xs text-muted-foreground">
                {t(
                  'agent.settings.phone_number.hint.translation',
                  'This is the number callers use to reach this agent.'
                )}
              </p>
            </div>
          ) : (
            <p className="px-1 py-2 text-sm text-muted-foreground">
              {t(
                'agent.settings.phone_number.empty.translation',
                'A phone number will appear here once your subscription is approved.'
              )}
            </p>
          )}
        </FormRow>
        <FormRow label={t('agent.new.description.translation')}>
          <DescriptionInput
            value={description}
            onChange={setDescription}
            disabled={disabled}
          />
        </FormRow>
        <FormRow
          label={t(
            'agent.new.notification_phone_number.translation',
            'Notification Phone Number'
          )}
        >
          <NotificationPhoneField
            countryCode={notificationCountryCode}
            localNumber={notificationLocalNumber}
            onCountryCodeChange={(code, adjustedLocalNumber) => {
              setNotificationCountryCode(code);
              setNotificationLocalNumber(adjustedLocalNumber);
            }}
            onLocalNumberChange={setNotificationLocalNumber}
            disabled={disabled}
            countryOptions={notificationCountryOptions}
          />
        </FormRow>
        <FormRow
          label={t(
            'agent.settings.notifications.title.translation',
            'Call Summary Emails'
          )}
        >
          <SummaryDigestField
            enabled={digestEnabled}
            hour={digestHour}
            onEnabledChange={setDigestEnabled}
            onHourChange={setDigestHour}
            disabled={disabled}
          />
        </FormRow>
        <FormRow label={t('agent.new.context.translation')}>
          <ContextInput
            value={context}
            onChange={setContext}
            disabled={disabled}
          />
        </FormRow>
      </div>
    </Card>
  );

  const hasRequestedSubscription = agent?.subscription?.status === 'requested';
  const planName = agent?.subscription?.plan_name || 'Unknown Plan';
  const requestedAt = agent?.subscription?.activated_at;
  const showPlans = showSubscriptionOptions && Boolean(agent);

  return (
    <div className="flex flex-col gap-6">
      {readOnly && renderReadOnlyBanner()}

      {showPlans &&
        (hasRequestedSubscription ? (
          <Card className={SECTION_CARD_CLASS}>
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="relative shrink-0">
                <Clock className="h-6 w-6 text-muted-foreground sm:h-7 sm:w-7" />
                <CheckCircle className="absolute -bottom-0.5 -right-0.5 h-4 w-4 text-green-600 sm:h-4 sm:w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-base font-semibold sm:text-lg">
                    {t(
                      'subscription.requested.title',
                      'Subscription Request Submitted'
                    )}
                  </h3>
                  <Badge variant="outline" className="text-xs">
                    {t('subscription.status.requested', 'Requested')}
                  </Badge>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div>
                    <span className="text-muted-foreground">
                      {t('subscription.requested.plan', 'Plan')}:{' '}
                    </span>
                    <span className="font-medium text-foreground">
                      {planName}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      {t('subscription.requested.agent', 'For Agent')}:{' '}
                    </span>
                    <span className="font-medium text-foreground">
                      {agent?.name}
                    </span>
                  </div>
                  {requestedAt && (
                    <div>
                      <span className="text-muted-foreground">
                        {t('subscription.requested.at', 'Requested on')}:{' '}
                      </span>
                      <span className="font-medium text-foreground">
                        {df.date(requestedAt)}
                      </span>
                    </div>
                  )}
                </div>
                {onCancelSubscription && (
                  <div className="pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        void onCancelSubscription();
                      }}
                      disabled={isCancellingSubscription}
                      className="rounded-lg"
                    >
                      {isCancellingSubscription ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t(
                            'subscription.requested.cancelling',
                            'Cancelling...'
                          )}
                        </>
                      ) : (
                        t(
                          'subscription.requested.cancel',
                          'Cancel Subscription Request'
                        )
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ) : (
          <SubscriptionPlans
            agentId={agentId}
            agentName={agent?.name || ''}
            onPlanSelect={handleSubscriptionRequested}
          />
        ))}

      {renderAgentDetailsForm(readOnly)}
      {!readOnly && (
        <div className="flex flex-col gap-3">
          <StickySaveBar
            isDirty={isDirty}
            isSaving={false}
            onSave={() => void handleSave()}
            onReset={handleReset}
            saveLabel={t('agent.settings.save_changes.translation')}
          />
          <Collapsible
            open={isMoreSettingsOpen}
            onOpenChange={setIsMoreSettingsOpen}
            className="flex w-full flex-col items-center"
          >
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-0.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {t('agent.settings.more_options.translation', 'More options')}
                <ChevronRight
                  className={cn(
                    'h-4 w-4 shrink-0 transition-transform duration-200',
                    isMoreSettingsOpen && 'rotate-90'
                  )}
                  aria-hidden
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent open={isMoreSettingsOpen}>
              <div
                ref={moreOptionsRef}
                className="flex flex-col items-center gap-2 pb-8 pt-3 scroll-mt-4"
              >
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteDialogOpenChange(true)}
                  disabled={hasActiveSubscription}
                  className="w-full sm:w-auto"
                >
                  {t('agent.settings.delete_agent.translation')}
                </Button>
                {hasActiveSubscription && (
                  <p className="max-w-sm px-1 text-center text-xs text-muted-foreground">
                    {t(
                      'agent.settings.delete_blocked.active_subscription.translation',
                      'Cancel the subscription before deleting this agent. Until the plan ends it keeps its phone number and keeps answering calls.'
                    )}
                  </p>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}

      <Dialog
        open={showDeleteDialog}
        onOpenChange={handleDeleteDialogOpenChange}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('agent.settings.delete_confirm.title.translation')}
            </DialogTitle>
            <DialogDescription>
              {t('agent.settings.delete_confirm.description.translation')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-2.5 rounded-lg bg-destructive/10 p-3">
              <AlertCircle
                className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                aria-hidden
              />
              <p className="text-sm text-destructive">
                {t(
                  'agent.settings.delete_confirm.data_warning.translation',
                  'Conversations, recordings, contacts, appointments, services and business hours for this agent go with it. This cannot be undone.'
                )}
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="delete-confirm-name">
                {t(
                  'agent.settings.delete_confirm.name_prompt.translation',
                  'Type the agent name to confirm'
                )}
              </Label>
              <Input
                id="delete-confirm-name"
                value={deleteConfirmName}
                onChange={event => setDeleteConfirmName(event.target.value)}
                disabled={isDeleting}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder={expectedDeleteName}
              />
              <p className="px-1 text-xs text-muted-foreground">
                {t(
                  'agent.settings.delete_confirm.name_hint.translation',
                  'Expected:'
                )}{' '}
                <span className="font-medium text-foreground">
                  {expectedDeleteName}
                </span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDeleteDialogOpenChange(false)}
              disabled={isDeleting}
            >
              {t('common.cancel.translation')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || !isDeleteNameConfirmed}
            >
              {isDeleting
                ? t('agent.settings.delete_confirm.deleting.translation')
                : t('common.actions.delete.translation')}
            </Button>
          </DialogFooter>
          {error && <div className="mt-2 text-sm text-red-500">{error}</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentSettings;
